
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Shield, LogOutIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { signOutAllSessions } from "@/contexts/auth/utils";
import { SPECIAL_ADMIN_EMAILS } from "@/contexts/auth/constants";

const UserMenu = () => {
  const { user, signOut, isAdmin, checkIsAdmin } = useAuth();
  const navigate = useNavigate();
  const [verifiedAdmin, setVerifiedAdmin] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSigningOutAllSessions, setIsSigningOutAllSessions] = useState(false);
  
  useEffect(() => {
    // Extra check to ensure admin status is correct for special users
    if (user?.email) {
      const email = user.email.toLowerCase();
      const isSpecialAdmin = SPECIAL_ADMIN_EMAILS.includes(email);
      
      if (isSpecialAdmin) {
        console.log("✅ Special admin recognized in UserMenu:", email);
        setVerifiedAdmin(true);
        
        // Refresh admin status in the background
        checkIsAdmin().then(status => {
          console.log("Admin status refreshed in UserMenu:", status);
          setVerifiedAdmin(status);
        });
      } else {
        console.log("Not a special admin in UserMenu:", email);
        setVerifiedAdmin(isAdmin);
      }
    }
  }, [user, isAdmin, checkIsAdmin]);
  
  // Get user's initials for avatar
  const getUserInitials = (): string => {
    if (!user?.email) return "?";
    
    // Get first letter of email
    return user.email.charAt(0).toUpperCase();
  };
  
  // Get display name
  const getDisplayName = (): string => {
    // Use email username part if available
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return "User";
  };
  
  // Handle profile click
  const handleProfileClick = () => {
    navigate("/profile");
  };
  
  // Handle admin dashboard click
  const handleAdminClick = () => {
    console.log("Navigating to admin dashboard. Admin status:", isAdmin, "Verified admin:", verifiedAdmin);
    navigate("/admin/users");
  };

  // Optimized logout handler with timeout protection
  const handleSignOut = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    
    try {
      setIsLoggingOut(true);
      
      // Add a timeout to force navigate if signOut takes too long
      const timeoutId = setTimeout(() => {
        console.log("Logout timeout reached, forcing navigation");
        navigate("/", { replace: true });
        setIsLoggingOut(false);
        toast.success("Logged out successfully");
      }, 2000); // 2 second timeout
      
      await signOut();
      clearTimeout(timeoutId);
      setIsLoggingOut(false);
      
      // Force navigate to home page after logout
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Logout failed. Please try again.");
      setIsLoggingOut(false);
    }
  };
  
  // Handle sign out all sessions
  const handleSignOutAllSessions = async () => {
    if (isSigningOutAllSessions) return; // Prevent multiple clicks
    
    try {
      setIsSigningOutAllSessions(true);
      toast.info("Signing out of all sessions...");
      
      // Add a timeout to force navigate if signOut takes too long
      const timeoutId = setTimeout(() => {
        console.log("Global logout timeout reached, forcing navigation");
        navigate("/", { replace: true });
        setIsSigningOutAllSessions(false);
        localStorage.clear(); // Clear local storage
        sessionStorage.clear(); // Clear session storage
        toast.success("Signed out of all sessions");
      }, 3000); // 3 second timeout
      
      await signOutAllSessions();
      await signOut();
      clearTimeout(timeoutId);
      
      // Clear any stored tokens
      localStorage.clear();
      sessionStorage.clear();
      
      setIsSigningOutAllSessions(false);
      
      // Force navigate to home page after logout
      navigate("/", { replace: true });
      toast.success("Signed out of all sessions successfully");
    } catch (error) {
      console.error("Error during global logout:", error);
      toast.error("Failed to sign out of all sessions. Please try again.");
      setIsSigningOutAllSessions(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{getUserInitials()}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="flex items-center justify-start gap-2 p-2">
          <div className="flex flex-col space-y-1 leading-none">
            <p className="font-medium">{getDisplayName()}</p>
            {user?.email && (
              <p className="text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleProfileClick}>
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        
        {(verifiedAdmin || isAdmin) && (
          <DropdownMenuItem onClick={handleAdminClick}>
            <Shield className="mr-2 h-4 w-4" />
            <span>Admin Dashboard</span>
          </DropdownMenuItem>
        )}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={handleSignOut} 
          disabled={isLoggingOut || isSigningOutAllSessions}
          className="relative"
        >
          {isLoggingOut ? (
            <>
              <Spinner size="sm" className="mr-2" />
              <span>Logging out...</span>
            </>
          ) : (
            <>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={handleSignOutAllSessions} 
          disabled={isLoggingOut || isSigningOutAllSessions}
          className="relative text-red-600 hover:text-red-700 focus:text-red-700"
        >
          {isSigningOutAllSessions ? (
            <>
              <Spinner size="sm" className="mr-2" />
              <span>Signing out all sessions...</span>
            </>
          ) : (
            <>
              <LogOutIcon className="mr-2 h-4 w-4" />
              <span>Sign out all sessions</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
